import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Badge } from '@components';
import type { Organization } from '../../../types/shared';

interface InvoiceBillingNoticeProps {
  organization: Organization | null;
}

export const InvoiceBillingNotice: React.FC<InvoiceBillingNoticeProps> = ({ organization }) => {
  const { t } = useTranslation();
  const pkg = organization?.active_package ?? null;

  return (
    <div className="space-y-6">
      {/* Invoice billing notice */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
              <span className="material-icons text-primary-600 dark:text-primary-400">
                receipt_long
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('billing.invoiceBilledTitle')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {t('billing.invoiceBilledDescription')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active package */}
      {pkg && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('billing.activePackage')}
              </h3>
              <Badge variant={pkg.contract_status === 'active' ? 'success' : 'default'} size="sm">
                {pkg.contract_status ? t(`billing.contractStatus.${pkg.contract_status}`, pkg.contract_status) : ''}
              </Badge>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-gray-500 dark:text-gray-400">
                  {t('billing.packageType')}
                </dt>
                <dd className="text-base font-medium text-gray-900 dark:text-white">
                  {pkg.contract_type ? t(`billing.contractType.${pkg.contract_type}`, pkg.contract_type) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500 dark:text-gray-400">
                  {t('billing.monthlyBaseFee')}
                </dt>
                <dd className="text-base font-medium text-gray-900 dark:text-white">
                  {pkg.contract_currency === 'EUR' ? '€' : `${pkg.contract_currency ?? ''} `}
                  {Number(pkg.monthly_base_fee).toFixed(2)}
                </dd>
                {Number(pkg.discount_percent ?? 0) > 0 && (
                  <dd className="text-sm text-gray-500 dark:text-gray-400">
                    {t('billing.baseFeeDiscount', {
                      price: Number(pkg.list_monthly_fee).toFixed(2),
                      discount: Number(pkg.discount_percent),
                    })}
                  </dd>
                )}
              </div>
              <div>
                <dt className="text-sm text-gray-500 dark:text-gray-400">
                  {t('billing.packageStartDate')}
                </dt>
                <dd className="text-base font-medium text-gray-900 dark:text-white">
                  {pkg.contract_start_date ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500 dark:text-gray-400">
                  {t('billing.packageTerm')}
                </dt>
                <dd className="text-base font-medium text-gray-900 dark:text-white">
                  {t('billing.packageTermMonths', { count: pkg.contract_term_months ?? 0 })}
                </dd>
              </div>
              {pkg.contract_minimum_term_end_date && (
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">
                    {t('billing.packageMinimumTermEnd')}
                  </dt>
                  <dd className="text-base font-medium text-gray-900 dark:text-white">
                    {pkg.contract_minimum_term_end_date}
                  </dd>
                </div>
              )}
            </dl>

            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
              {t('billing.baseFeeExcludesUsageNotice')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
